/**
 * WorkforceConnect — the HR domain's Workforce card in the Domain agents panel.
 *
 * Auth goes through Workforce's REST API (org lookup / creation by the user's email), then Lyra connects
 * Workforce's MCP for the chosen org so the chat can run HR work through it.
 *
 * The plug button in the card's corner is the connection control:
 *   green plug  = connected (MCP verified)      -> click disconnects
 *   amber plug  = linked but MCP not reachable  -> click disconnects
 *   grey slashed plug = disconnected            -> click signs in again: shows the user's orgs (or create one)
 *
 * States: no connection yet -> org list / create form (first connection); connected -> compact card with the
 * "Use in chat" switch and an Enter Workforce link; user just disconnected -> grey card waiting for the plug click.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Users } from 'lucide-react';
import { T2 } from './AppShellV2.tsx';
import { agenticHrAPI, connectorsAPI } from '../../services/api.js';
import AgentCardShell, { ConnectButton, PlugButton, StatusPill, UseInChatSwitch } from './AgentCardShell.tsx';
import { domainStyleFor, domainCopyFor } from './domainStyle.ts';
import type { DomainAgent } from '../../hooks/useDomainAgents.ts';

type Org = { id: string; name: string };
type Connection = { organizationId: string; organizationName: string; connected: boolean };
/** Result of a live MCP check; null while it has not run / is running. */
type McpCheck = { ok: boolean; toolCount: number; error: string | null };

const orgRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', border: `1px solid ${T2.border}`, borderRadius: 11, background: T2.surface };
const softBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: 'none', borderRadius: 10, padding: '8px 12px', background: T2.accentSoft, color: T2.accentText, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const ghostBtn: React.CSSProperties = { ...softBtn, background: 'transparent', border: `1px solid ${T2.border}`, color: T2.ink2 };

const notVerified = (e: any): { email?: string } | null => (e?.response?.data?.code === 'EMAIL_NOT_VERIFIED' ? { email: e.response.data.email } : null);
const errorMessage = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

export default function WorkforceConnect({ onConnectionChange, agent, selected = false, onToggleSelect }: {
  onConnectionChange?: (connected: boolean) => void;
  /** Whether this agent is selected for the chat, and the toggle (offered once the MCP is verified). */
  selected?: boolean;
  onToggleSelect?: () => void;
  /** The connected Workforce MCP agent (from /api/domain-agents); its name is shown on the card. */
  agent?: DomainAgent | null;
}) {
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [connection, setConnection] = useState<Connection | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [appUrl, setAppUrl] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // After the user disconnects, the card waits (grey slashed plug) until they click it to sign in again.
  const [disconnected, setDisconnected] = useState(false);
  // 'oauth2': Workforce is connected by approving on its own site (it knows who you are and which organization you mean).
  // 'service': the older path where Lyra vouches for your verified email and you pick the organization here.
  const [mode, setMode] = useState<'oauth2' | 'service'>('service');
  // Connecting needs a verified email: set when the server says so.
  const [unverified, setUnverified] = useState<{ email?: string } | null>(null);
  // The parent's callback is read through a ref so a new function identity on every parent render can never
  // re-trigger loading (status/verify) — that was an infinite request loop.
  const onChangeRef = useRef(onConnectionChange);
  onChangeRef.current = onConnectionChange;
  const [mcpCheck, setMcpCheck] = useState<McpCheck | null>(null);
  const [verifying, setVerifying] = useState(false);
  const { Icon, bg, fg } = domainStyleFor('hr');

  // Really connects to the Workforce MCP (not just "an org is linked") and reports tools or the reason it failed.
  const verify = useCallback(async () => {
    setVerifying(true);
    try {
      const res = await agenticHrAPI.verify();
      setMcpCheck({ ok: !!res.ok, toolCount: res.toolCount || 0, error: res.error || null });
      onChangeRef.current?.(!!res.ok);
    } catch (e) {
      setMcpCheck({ ok: false, toolCount: 0, error: errorMessage(e, 'Could not reach Workforce.') });
      onChangeRef.current?.(false);
    } finally {
      setVerifying(false);
    }
  }, []);

  const load = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const st = await agenticHrAPI.status();
      setAppUrl(st.appUrl || null);
      let conn = st.connection || null;
      let nextMode: 'oauth2' | 'service' = 'service';
      try {
        const { connectors } = await connectorsAPI.list();
        const wf = (connectors || []).find((c: any) => c.id === 'workforce');
        if (wf?.auth === 'oauth2') { nextMode = 'oauth2'; conn = wf.connected ? { organizationId: 'oauth', organizationName: 'Workforce', connected: true } : null; }
      } catch { /* the connector list is optional: without it the card keeps working the older way */ }
      setMode(nextMode);
      setConnection(conn);
      setMcpCheck(null);
      if (conn) verify();
      else onChangeRef.current?.(false);
      if (!conn && nextMode === 'service') {
        try {
          const res = await agenticHrAPI.organizations();
          setUnverified(null);
          setOrgs(res.organizations || []);
          setAppUrl((u) => res.appUrl || u);
        } catch (e) {
          const nv = notVerified(e);
          if (!nv) throw e;
          setUnverified(nv);
        }
      }
      setPhase('ready');
    } catch (e) {
      setError(errorMessage(e, 'Workforce is unavailable right now.'));
      setPhase('error');
    }
  }, [verify]);

  useEffect(() => { load(); }, [load]);

  // Coming back from Workforce's approval page: show why it failed, then tidy the address bar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const failure = params.get('connectError');
    if (failure) setError(failure);
    if (failure || params.get('connected')) {
      params.delete('connectError'); params.delete('connected');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Runs `work` (connect). With openApp it first opens a tab synchronously (so pop-up blockers allow it), then
  // points it at Workforce once done; without it (choosing an organization) it only connects.
  const enter = async (work: () => Promise<Connection | void>, key: string, openApp = true) => {
    const tab = openApp ? window.open('', '_blank') : null;
    setBusy(key);
    setError(null);
    try {
      const next = await work();
      if (next) {
        setConnection(next);
        setDisconnected(false);
        verify();
      }
      if (tab && appUrl) { tab.location.href = appUrl; tab.opener = null; }
      else tab?.close();
    } catch (e) {
      tab?.close();
      const nv = notVerified(e);
      if (nv) setUnverified(nv); else setError(errorMessage(e, 'Could not connect to Workforce.'));
    } finally {
      setBusy(null);
    }
  };

  // OAuth: send the browser to Workforce to approve; it comes back to Core, which stores the connection.
  const startOAuth = async () => {
    setBusy('oauth');
    setError(null);
    try {
      const r = await connectorsAPI.connect('workforce');
      if (r.authorizeUrl) { window.location.assign(r.authorizeUrl); return; }
      setError('Workforce did not return a sign-in address.');
    } catch (e) {
      setError(errorMessage(e, 'Could not start the Workforce connection.'));
    }
    setBusy(null);
  };

  const connectOrg = (org: Org) =>
    enter(async () => (await agenticHrAPI.connect(org.id, org.name)).connection, org.id, false);

  const createOrg = () => {
    const name = orgName.trim();
    if (!name) return;
    return enter(async () => {
      const { organization } = await agenticHrAPI.createOrganization(name);
      return (await agenticHrAPI.connect(organization.id, organization.name)).connection;
    }, 'create', false);
  };

  // Green/amber plug: removes the connection (MCP server, org link) on the server.
  const disconnect = async () => {
    setBusy('disconnect');
    setError(null);
    try {
      if (mode === 'oauth2') await connectorsAPI.disconnect('workforce'); else await agenticHrAPI.unlink();
      setConnection(null);
      setMcpCheck(null);
      setDisconnected(true);
      onChangeRef.current?.(false);
    } catch (e) {
      setError(errorMessage(e, 'Could not disconnect Workforce.'));
    } finally {
      setBusy(null);
    }
  };

  // Grey plug: sign in again with the AgentLyra login — look up the user's orgs, exactly like the first connection.
  const startReconnect = () => {
    setDisconnected(false);
    load();
  };

  const mcpOk = !!mcpCheck?.ok;
  const needsReconnect = !!connection && (!connection.connected || (!!mcpCheck && !mcpCheck.ok));
  const alert = error && <div role="alert" style={{ fontSize: 12, color: 'var(--lyra-color-danger)' }}>{error}</div>;

  // ── Disconnected (user clicked the plug): grey slashed plug, waits for a click ──────────────────────────
  if (disconnected && !connection) {
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell
          Icon={Icon} bg="var(--app-surface-3)" fg="var(--lyra-color-text-muted)" name="Workforce" subtitle="Disconnected"
          plug={<PlugButton state="disconnected" label="Reconnect Workforce" onClick={startReconnect} />}
        >
          <StatusPill tone="muted">Click Connect to sign in again</StatusPill>
          {alert}
        </AgentCardShell>
      </div>
    );
  }

  // ── Loading / error ────────────────────────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell Icon={Icon} bg={bg} fg={fg} name="Workforce" subtitle="HR" plug={<PlugButton state="checking" label="Checking Workforce" />}>
          <StatusPill tone="muted">Checking Workforce…</StatusPill>
        </AgentCardShell>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell Icon={Icon} bg="var(--app-surface-3)" fg="var(--lyra-color-text-muted)" name="Workforce" subtitle="HR" plug={<PlugButton state="disconnected" label="Workforce unavailable" />}>
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: T2.ink2 }}>
            <span style={{ flex: 1 }}><strong style={{ color: T2.ink }}>Workforce is unavailable.</strong> {error}</span>
            <button onClick={load} style={ghostBtn}>Retry</button>
          </div>
        </AgentCardShell>
      </div>
    );
  }

  // ── Connected (or linked but unreachable) ──────────────────────────────────────────────────────────────
  if (connection) {
    const plugState = mcpOk ? 'connected' : mcpCheck || !connection.connected ? 'warning' : 'checking';
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell
          Icon={Icon} bg={bg} fg={fg} name={agent?.name || 'Workforce'} subtitle={`${connection.organizationName} · MCP`}
          selected={selected && mcpOk}
          plug={<PlugButton state={plugState} label="Disconnect Workforce" onClick={disconnect} disabled={!!busy || verifying} />}
        >
          {mcpOk ? (
            <StatusPill tone="ok">Connected · {mcpCheck!.toolCount} tools</StatusPill>
          ) : mcpCheck ? (
            <StatusPill tone="warn">Workforce MCP not reachable — {mcpCheck.error}</StatusPill>
          ) : !connection.connected ? (
            <StatusPill tone="warn">Connection needs attention</StatusPill>
          ) : (
            <StatusPill tone="muted">{verifying ? 'Verifying MCP connection…' : 'Checking…'}</StatusPill>
          )}
          {agent && onToggleSelect && (
            <UseInChatSwitch on={selected} onToggle={onToggleSelect} disabled={!mcpOk} hint="Available once the Workforce MCP is reachable" />
          )}
          {needsReconnect ? (
            <button
              onClick={() => (mode === 'oauth2' ? startOAuth() : enter(async () => (await agenticHrAPI.connect(connection.organizationId, connection.organizationName)).connection, 'reconnect'))}
              disabled={!!busy}
              style={softBtn}
            >
              {busy === 'reconnect' ? 'Reconnecting…' : 'Reconnect'}
            </button>
          ) : (
            <button onClick={() => enter(async () => undefined, 'enter')} disabled={!!busy || !appUrl} style={ghostBtn} aria-label={`Enter Workforce (${connection.organizationName})`}>
              Enter Workforce <ArrowRight size={13} />
            </button>
          )}
          {alert}
        </AgentCardShell>
      </div>
    );
  }

  // ── OAuth mode, not connected: one button, the rest happens on Workforce ─────────────────────────────────
  if (mode === 'oauth2' && !connection) {
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell Icon={Icon} bg={bg} fg={fg} name="Workforce" subtitle={domainCopyFor('hr', 'Sign in on Workforce')}
          plug={<ConnectButton onClick={startOAuth} disabled={!!busy} label="Connect Workforce">{busy === 'oauth' ? 'Opening…' : 'Connect'}</ConnectButton>}>
          <div style={{ fontSize: 12, color: T2.ink3, lineHeight: 1.5 }}>You will sign in on Workforce and choose your organization there. Lyra never sees your Workforce password.</div>
          {alert}
        </AgentCardShell>
      </div>
    );
  }

  // ── Email not verified: connecting hands out access based on who the address belongs to, so prove it first ──
  if (unverified && !connection) {
    return (
      <div style={{ marginBottom: 12 }}>
        <AgentCardShell Icon={Icon} bg={bg} fg={fg} name="Workforce" subtitle="Verify your email to connect" plug={<PlugButton state="disconnected" label="Not connected" />}>
          <div role="status" style={{ fontSize: 12.5, color: T2.ink2, lineHeight: 1.5 }}>
            Workforce shares your organizations by email address, so Lyra needs to know <strong style={{ color: T2.ink }}>{unverified.email || 'your address'}</strong> is yours before connecting.
          </div>
          <div style={{ fontSize: 12, color: T2.ink3, lineHeight: 1.5 }}>Sign out and sign in again: Lyra will email you a verification link.</div>
          <button onClick={() => { setUnverified(null); load(); }} style={softBtn}>I have verified, continue</button>
        </AgentCardShell>
      </div>
    );
  }

  // ── No connection: pick one of the user's orgs, or create one (first connection / signing in again) ───
  return (
    <div style={{ marginBottom: 12 }}>
      <AgentCardShell
        Icon={Icon} bg={bg} fg={fg} name="Workforce"
        subtitle={orgs.length === 0 ? 'Create your organization' : 'Choose your organization'}
        plug={<PlugButton state="disconnected" label="Not connected" />}
      >
        {orgs.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orgs.map((o) => (
              <div key={o.id} style={orgRow}>
                <div style={{ minWidth: 0, flex: 1, fontSize: 13, fontWeight: 600, color: T2.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</div>
                <button onClick={() => connectOrg(o)} disabled={!!busy} style={softBtn} aria-label={`Connect ${o.name}`}>
                  {busy === o.id ? 'Connecting…' : 'Connect'} <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createOrg()}
              placeholder="Name your organization"
              aria-label="Organization name"
              style={{ flex: 1, minWidth: 150, border: `1px solid ${T2.border}`, borderRadius: 10, padding: '8px 11px', fontSize: 13, fontFamily: 'inherit', color: T2.ink, background: T2.surface, outline: 'none' }}
            />
            <button onClick={createOrg} disabled={!orgName.trim() || !!busy} style={{ ...softBtn, opacity: orgName.trim() && !busy ? 1 : 0.6 }}>
              {busy === 'create' ? 'Creating…' : 'Create & connect'} <ArrowRight size={13} />
            </button>
          </div>
        )}
        {alert}
      </AgentCardShell>
    </div>
  );
}
